<template>
  <transition
    name="dialog-fade"
    @after-enter="afterEnter"
    @after-leave="afterLeave">
    <div class="zm-dialog__wrapper" v-show="visible" @click.self="handleWrapperClick">
      <div
        class="zm-dialog"
        :class="[{ 'is-fullscreen': fullscreen, 'zm-dialog--center': center }, customClass]"
        ref="dialog"
        role="dialog"
        :aria-describedby="(visible && ariaDescribedby) ? ariaDescribedby : null"
        :aria-labelledby="visible ? (ariaLabelledby ? ariaLabelledby : dialogId) : null"
        v-bind="$attrs"
        :style="style">
        <div class="zm-dialog__header">
          <slot name="title">
            <span class="zm-dialog__title" :id="dialogId">{{ title }}</span>
          </slot>
          <button
            type="button"
            class="zm-dialog__headerbtn"
            aria-label="Close"
            v-if="showClose"
            @click="handleClose">
            <i class="zm-dialog__close zm-icon zm-icon-close"></i>
          </button>
        </div>
        <div class="zm-dialog__body" v-if="rendered">
          <slot></slot>
        </div>
        <div class="zm-dialog__footer" v-if="$slots.footer">
          <slot name="footer"></slot>
        </div>
      </div>
    </div>
  </transition>
</template>

<script type="text/babel">
  import Popup from '../../utils/popup';
  import emitter from '../../mixins/emitter';
  import { generateId } from '../../utils/util';
  import AriaDialog from '../../utils/aria-dialog';

  export default {
    name: 'ZmDialog',

    mixins: [Popup, emitter],

    props: {
      title: {
        type: String,
        default: ''
      },

      modal: {
        type: Boolean,
        default: true
      },

      modalAppendToBody: {
        type: Boolean,
        default: true
      },

      appendToBody: {
        type: Boolean,
        default: false
      },

      lockScroll: {
        type: Boolean,
        default: true
      },

      closeOnClickModal: {
        type: Boolean,
        default: false
      },

      closeOnPressEscape: {
        type: Boolean,
        default: true
      },

      showClose: {
        type: Boolean,
        default: false
      },

      width: String,

      fullscreen: Boolean,

      customClass: {
        type: String,
        default: ''
      },

      top: {
        type: String,
        default: '15vh'
      },
      beforeClose: Function,
      center: {
        type: Boolean,
        default: false
      },
      ariaDescribedby: {
        type: String,
        default: ''
      },
      ariaLabelledby: {
        type: String,
        default: ''
      }
    },
    inheritAttrs: false,
    data() {
      return {
        closed: false,
        dialogId: `dialog-title-${generateId()}-${generateId()}`
      };
    },

    watch: {
      visible(val) {
        if (val) {
          this.closed = false;
          this.$emit('open');
          this.$el.addEventListener('scroll', this.updatePopper);
          this.$nextTick(() => {
            this.$refs.dialog.scrollTop = 0;
          });
          if (this.appendToBody) {
            document.body.appendChild(this.$el);
          }
        } else {
          this.$el.removeEventListener('scroll', this.updatePopper);
          if (!this.closed) this.$emit('close');
        }
      }
    },

    computed: {
      style() {
        let style = {};
        if (!this.fullscreen) {
          style.marginTop = this.top;
          if (this.width) {
            style.width = this.width;
          }
        }
        return style;
      }
    },

    methods: {
      getFirstFocus() {
        const focusFirst = this.$el.querySelector('[focusFirst]');
        if (focusFirst) {
          return focusFirst;
        }
        return null;
      },
      handleWrapperClick() {
        if (!this.closeOnClickModal) return;
        this.handleClose();
      },
      handleClose() {
        if (typeof this.beforeClose === 'function') {
          this.beforeClose(this.hide);
        } else {
          this.hide();
        }
      },
      hide(cancel) {
        if (cancel !== false) {
          this.$emit('update:visible', false);
          this.$emit('close');
          this.closed = true;
        }
      },
      updatePopper() {
        this.broadcast('ZmSelectDropdown', 'updatePopper');
        this.broadcast('ZmDropdownMenu', 'updatePopper');
      },
      afterEnter() {
        this.$emit('opened');
        const focusAfterClosed = document.activeElement;
        const firstFocus = this.getFirstFocus();
        this.$nextTick(() => {
          this.ariaDialog = new AriaDialog(this.$refs.dialog, focusAfterClosed, firstFocus);
        });
      },
      afterLeave() {
        this.$emit('closed');
        this.ariaDialog && this.ariaDialog.closeDialog();
        this.ariaDialog = null;
      },
      refreshAria() {
        this.$nextTick(() => {
          this.ariaDialog && this.ariaDialog.refresh();
        });
      }
    },

    mounted() {
      if (this.visible) {
        this.rendered = true;
        this.open();
        if (this.appendToBody) {
          document.body.appendChild(this.$el);
        }
      }
    },

    destroyed() {
      // if appendToBody is true, remove DOM node after destroy
      if (this.appendToBody && this.$el && this.$el.parentNode) {
        this.$el.parentNode.removeChild(this.$el);
      }
      this.ariaDialog = null;
    }
  };
</script>

<style lang="scss">
  @import "../../scss/mixins/mixins";
  @import "../../scss/mixins/utils";
  @import "../../scss/common/var";
  @import "../../scss/common/popup";

  @include b(dialog) {
    position: relative;
    margin: 0 auto 50px;
    background: $--color-white;
    border-radius: $--border-radius-base;
    box-shadow: $--dialog-box-shadow;
    border: 1px solid transparent;
    box-sizing: border-box;
    width: 50%;
    padding: $--dialog-padding-horizontal $--dialog-padding-horizontal $--dialog-padding-vertical;
    @include when(fullscreen) {
      width: 100%;
      margin-top: 0;
      margin-bottom: 0;
      height: 100%;
      overflow: auto;
    }

    @include e(wrapper) {
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      overflow: auto;
      margin: 0;
    }

    @include e(header) {
      padding-bottom: 16px;
    }

    @include e(headerbtn) {
      position: absolute;
      top: $--dialog-padding-horizontal;
      right: $--dialog-padding-horizontal;
      padding: 0;
      background: transparent;
      border: none;
      outline: none;
      cursor: pointer;
      font-size: $--message-close-size;

      .zm-dialog__close {
        color: $--color-info;
      }

      &:focus, &:hover {
        .zm-dialog__close {
          color: $--color-primary;
        }
      }
    }

    @include e(title) {
      line-height: $--dialog-line-height;
      font-size: $--dialog-title-font-size;
      color: $--color-text-primary;
      word-break: break-all;
    }

    @include e(body) {
      padding-bottom: $--dialog-padding-vertical;
      color: $--color-text-primary;
    }

    @include e(footer) {
      padding-top: $--dialog-padding-vertical;
      text-align: right;
      box-sizing: border-box;
    }

    // 内容居中布局
    @include m(center) {
      text-align: center;

      @include e(body) {
        text-align: initial;
        padding: 25px 0 30px;
      }

      @include e(footer) {
        text-align: inherit;
      }
    }

  }

  @media screen and (max-width: 720px) {
    .zm-dialog {
      width: 95% !important;
      margin-top: 6% !important;
      margin-bottom: 2% !important;
      height: 86%;
      overflow: auto;
      display: flex;
      flex-direction: column;
      padding: 15px;
      .zm-dialog__header {
        .zm-dialog__title {
          font-size: 18px;
        }
      }
      .zm-dialog__body {
        flex: 1;
        overflow: auto;
      }
    }
  }

  .dialog-fade-enter-active {
    animation: dialog-fade-in .3s;
  }

  .dialog-fade-leave-active {
    animation: dialog-fade-out .3s;
  }

  @keyframes dialog-fade-in {
    0% {
      transform: translate3d(0, -20px, 0);
      opacity: 0;
    }
    100% {
      transform: translate3d(0, 0, 0);
      opacity: 1;
    }
  }

  @keyframes dialog-fade-out {
    0% {
      transform: translate3d(0, 0, 0);
      opacity: 1;
    }
    100% {
      transform: translate3d(0, -20px, 0);
      opacity: 0;
    }
  }
</style>
